import { EKeyCode } from '@/enums/e_key_code';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { service_drop } from '@/services/drag_and_drop/SERVICE__drop';
import { LOCAL__ui_set_string } from '@/state/ui/_base/LOCAL__ui_set_string';
import { LOCAL__ui_generation_graph_set_hovered_field } from '@/state/ui/generation/LOCAL__ui_generation_graph_set_hovered_field';
import { UTILS__generation } from '@/utils/UTILS__generation';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { UTILS__graph_ui } from '@/utils/UTILS__graph_ui';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { assert_string_uuid } from '@shared/asserts/utils_asserts_string';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { DTO_GRAPH__edge } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__edge';
import {
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  EdgeChange,
  MiniMap,
  Node,
  NodeChange,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useMemo } from 'react';
import { HOOK__get_div_size } from '../../_hooks/HOOK__get_div_size';
import { G_BAR__generation_graph_actions } from '../_base/bar_graph_actions/G_BAR__generation_graph_actions';
import { HOOK_GRAPH_FLOW__connections } from './_hooks/HOOK_GRAPH_FLOW__connections';
import { HOOK_REACT_FLOW__custom_cursors } from './_hooks/HOOK_REACT_FLOW__custom_cursors';
import { FN_GRAPH_UPDATE__edge_add__by_change } from './_methods/FN_GRAPH_UPDATE__edge_add__by_change';
import { FN_GRAPH_UPDATE__edge_remove } from './_methods/FN_GRAPH_UPDATE__edge_remove';
import { FN_GRAPH_UPDATE__edge_remove__by_change } from './_methods/FN_GRAPH_UPDATE__edge_remove__by_change';
import { FN_GRAPH_UPDATE__edge_remove__by_event } from './_methods/FN_GRAPH_UPDATE__edge_remove__by_event';
import { FN_GRAPH_UPDATE__node_position__by_change } from './_methods/FN_GRAPH_UPDATE__node_position__by_change';
import { FN_GRAPH_UPDATE__node_remove } from './_methods/FN_GRAPH_UPDATE__node_remove';
import { FN_GRAPH_UPDATE__node_remove__by_change } from './_methods/FN_GRAPH_UPDATE__node_remove__by_change';
import { FN_GRAPH_UPDATE__node_remove__by_event } from './_methods/FN_GRAPH_UPDATE__node_remove__by_event';
import { FN_GRAPH_UPDATE__node_select__by_change } from './_methods/FN_GRAPH_UPDATE__node_select__by_change';
import { WATCHER__xyflow_drop_node_invocation } from './_watchers/WATCHER__xyflow_drop_node_invocation';
import { WATCHER__xyflow_fit_view } from './_watchers/WATCHER__xyflow_fit_view';
import { WATCHER__xyflow_node_focus } from './_watchers/WATCHER__xyflow_node_focus';
import { G_GRAPH__edge_default } from './G_GRAPH__edge_default';
import { G_GRAPH_NODE__invocation } from './node/G_GRAPH_NODE__invocation';

export function G_GRAPH__flow() {
  /*
   * STORE
   */
  const dispatch = use_dispatch();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_graph__node_id_hovered, g_graph__node_id_selected, g_graph__edge_id_selected, g_general__ui_disabled } = use_slice_ui([
    'g_graph__node_id_hovered',
    'g_graph__node_id_selected',
    'g_graph__edge_id_selected',
    'g_general__ui_disabled',
  ]);

  /*
   * STATE
   */
  const { graph } = generation_current.tool;

  let { ref_element__set, size: container_size, div_element: ref_container } = HOOK__get_div_size();
  HOOK_REACT_FLOW__custom_cursors();

  const nodeTypes = useMemo(() => ({ invocation: G_GRAPH_NODE__invocation }), []);
  const edgeTypes = useMemo(() => ({ default: G_GRAPH__edge_default }), []);

  const { onConnectStart, onConnectEnd, onConnect } = HOOK_GRAPH_FLOW__connections();

  const create_xyflow_nodes_cached = useCallback(() => create_xyflow_nodes(), [graph]);
  const create_xyflow_edges_cached = useCallback(() => create_xyflow_edges(), [graph, g_graph__node_id_selected, g_graph__edge_id_selected]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  /*
   * EFFECTS
   */
  use_effect(() => {
    const nodes_out = create_xyflow_nodes_cached();
    const edges_out = create_xyflow_edges_cached();

    setNodes(nodes_out);
    setEdges(edges_out);
  }, [graph, setNodes, setEdges, g_graph__node_id_selected, g_graph__edge_id_selected, create_xyflow_nodes_cached, create_xyflow_edges_cached]);

  /*
   * EVENTS
   */

  const onNodesChangeInternal = useCallback(
    (changes: NodeChange<Node>[]) => void process_changes_node(changes),

    [graph, onNodesChange, process_changes_node],
  );
  const onEdgesChangeInternal = useCallback(
    (changes: EdgeChange<Edge>[]) => void process_changes_edge(changes),

    [graph, onEdgesChange],
  );

  function onNodeMouseEnter(e: React.MouseEvent, node: Node) {
    assert_string_uuid(node.id);

    dispatch(LOCAL__ui_set_string({ value: node.id, key: 'g_graph__node_id_hovered', valid_values: [] }));
  }

  function onNodeMouseLeave(e: React.MouseEvent, node: Node) {
    if (g_graph__node_id_hovered != node.id) return;

    dispatch(LOCAL__ui_set_string({ value: ENUM__string_id_empty.UUID, key: 'g_graph__node_id_hovered', valid_values: [] }));
    dispatch(LOCAL__ui_generation_graph_set_hovered_field()); //reset hovered  field
  }

  async function onDelete({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
    if (g_general__ui_disabled) return;

    let graph_new = graph.clone();

    let is_updated = FN_GRAPH_UPDATE__edge_remove(edges, graph_new);
    is_updated = FN_GRAPH_UPDATE__node_remove(nodes, graph_new) || is_updated;

    if (is_updated) {
      await UTILS__graph_data.update(graph_new);

      await UTILS__generation.update_tool_name_as_custom(generation_current.tool);
    }
  }

  async function onKeyDown(e: React.KeyboardEvent) {
    if (e.key == EKeyCode.Backspace || e.key == EKeyCode.Delete) {
      let graph_new = graph.clone();

      let is_updated = FN_GRAPH_UPDATE__node_remove__by_event(graph_new);

      is_updated = FN_GRAPH_UPDATE__edge_remove__by_event(graph_new) || is_updated;

      if (is_updated) {
        await UTILS__graph_data.update(graph_new);

        await UTILS__generation.update_tool_name_as_custom(generation_current.tool);
      }
    }
  }

  function onEdgeClick(e: React.MouseEvent, edge: Edge) {
    e?.stopPropagation();

    let { data } = edge;

    assert_instanceof(data, DTO_GRAPH__edge);

    UTILS__graph_ui.edge__select(data.edge_id);
  }

  /*
   * METHODS
   */

  function create_xyflow_nodes() {
    return graph.nodes_map.values().map(node => node.to_xyflow_format());
  }

  function create_xyflow_edges() {
    let edges = graph.edges;

    let out = edges.map(edge => edge.to_xyflow_format(g_graph__edge_id_selected));

    return out;
  }

  async function process_changes_edge(changes: EdgeChange<Edge>[]) {
    if (g_general__ui_disabled) return;

    onEdgesChange(changes);

    console.log('process_changes_edge', changes);

    let is_updated = false;

    let graph_new = graph.clone();

    for (const change of changes) {
      is_updated = FN_GRAPH_UPDATE__edge_remove__by_change(change, graph_new) || is_updated;
      is_updated = FN_GRAPH_UPDATE__edge_add__by_change(change, graph_new) || is_updated;
    }

    if (is_updated) {
      await UTILS__graph_data.update(graph_new);

      await UTILS__generation.update_tool_name_as_custom(generation_current.tool);
    }
  }

  async function process_changes_node(changes: NodeChange<Node>[]) {
    /*
     * WITHOUT GRAPH UPDATE
     */

    for (const change of changes) {
      FN_GRAPH_UPDATE__node_select__by_change(change);
    }

    /*
     * WITH GRAPH UPDATE
     */
    if (g_general__ui_disabled) return;

    onNodesChange(changes);

    let is_updated = false;

    let graph_new = graph.clone();

    console.log('process_changes_node', changes);
    for (const change of changes) {
      is_updated = FN_GRAPH_UPDATE__node_position__by_change(change, graph_new) || is_updated;
      is_updated = FN_GRAPH_UPDATE__node_remove__by_change(change, graph_new) || is_updated;
    }

    if (is_updated) {
      await UTILS__graph_data.update(graph_new);

      await UTILS__generation.update_tool_name_as_custom(generation_current.tool);
    }
  }

  /*
   * HTML
   */

  return (
    <ReactFlowProvider>
      <div className="flex-1 h-full" ref={ref_element__set}>
        <WATCHER__xyflow_drop_node_invocation {...{ div_container: ref_container, container_size }} />
        <WATCHER__xyflow_node_focus />
        <WATCHER__xyflow_fit_view />

        <ReactFlow
          {...{ nodes, edges }}
          minZoom={0.2}
          maxZoom={5}
          onDragEnd={e => {
            service_drop.resolve_drop(e.clientX, e.clientY);
          }}
          onEdgeClick={onEdgeClick}
          onNodesChange={onNodesChangeInternal}
          onEdgesChange={onEdgesChangeInternal}
          {...{ onNodeMouseEnter, onNodeMouseLeave }}
          {...{ nodeTypes, edgeTypes, onConnect, onConnectStart, onConnectEnd, onDelete }}
          edgesFocusable={true}
          selectionMode={SelectionMode.Partial}
          className="size-full bg-bar"
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={[]}
          onKeyDown={onKeyDown}
          defaultEdgeOptions={{
            type: 'default',
            animated: true,
            // markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#ffffff' },
          }}
          isValidConnection={connection => {
            return connection.source !== connection.target;
          }}
          snapToGrid
          snapGrid={[CONSTANTS__shared.GRID_CELL_SIZE, CONSTANTS__shared.GRID_CELL_SIZE]}
        >
          <Background size={3} color="var(--component)" variant={BackgroundVariant.Dots} gap={CONSTANTS__shared.GRID_CELL_SIZE} offset={1} />
          <Controls>
            <G_BAR__generation_graph_actions />
          </Controls>
          <MiniMap nodeStrokeWidth={3} zoomable pannable className="!m-1.5 !rounded-lg " nodeColor="#4b5563" maskColor="rgba(0,0,0,0.5)" />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
}
